// @flow

import * as React from 'react';
import styled, { keyframes, css } from 'styled-components';
import AmpContext from 'utils/AmpContext';
import type { ImageData } from 'api/shared';
import { imageUrl, getWebpSrc } from 'utils/filestack';
import Box from 'components/Box';
import NoAmp from 'containers/NoAmp';
import Icon from 'components/Icon';
import ScrollContext from 'containers/ScrollContext';
import { fontWeights, colors, transition } from '../theme';

const { useState, useEffect, useRef, useContext, useCallback } = React;

export const getSrc = ({
  handle,
  width,
  height,
  round,
  fit,
  format,
  quality
}: {
  handle: string,
  width?: number,
  height?: number,
  round?: boolean,
  fit?: string,
  format?: string,
  quality?: number
}) => {
  const dimensions = { w: width, h: height };
  const resize = Object.keys(dimensions)
    .map(key => (dimensions[key] ? `${key}:${dimensions[key]}` : ''))
    .filter(item => item.length > 0)
    .join(',');
  return imageUrl({
    handle,
    transform: `${
      resize.length > 0 ? `resize=${resize}${fit ? `,fit:${fit}` : ''}` : ''
    }/quality=value:${quality || 70}${round ? '/circle' : ''}${
      format ? `/output=format:${format}` : ''
    }`
  });
};

type ImageProps = {
  height?: number | string,
  width?: number | string,
  src: string,
  srcSet?: string,
  alt: string,
  className?: string,
  className?: string,
  webp?: boolean,
  quality?: number
};

const BaseImg = React.forwardRef<ImageProps, HTMLImageElement>(
  ({ className, src, srcSet, webp, quality, ...props }: ImageProps, ref) => {
    const webpSrc = getWebpSrc(srcSet || src, { alwaysConvert: webp, quality });

    return (
      <picture>
        <source srcSet={webpSrc} type="image/webp" />
        <source srcSet={srcSet || src} />
        <img src={src} {...props} ref={ref} />
      </picture>
    );
  }
);

type ImgProps = ImageProps & {
  alwaysShow?: boolean,
  className?: string,
  src: string,
  srcSet?: string,
  layout?: string,
  onLoad?: Function,
  quality?: number
};

const Img = React.forwardRef<ImgProps, HTMLImageElement>(
  (
    {
      layout,
      className,
      src,
      srcSet,
      alwaysShow,
      onLoad,
      quality,
      ...props
    }: ImgProps,
    ref
  ) => {
    const [show, setShow] = useState(!!alwaysShow);
    const [, scrollY] = useContext(ScrollContext);
    const lazyRef = useRef(null);

    const update = useCallback(
      () => {
        if (!show && lazyRef && lazyRef.current) {
          const offsetY = lazyRef.current.getBoundingClientRect().top - 200;
          const inView = offsetY < window.innerHeight + scrollY;
          if (inView && !show) {
            setShow(true);
          }
        }
      },
      [scrollY, show]
    );

    useEffect(
      () => {
        update();
      },
      [scrollY, update]
    );

    return (
      <AmpContext.Consumer>
        {requestIsAmp =>
          requestIsAmp ? (
            <amp-img
              layout={layout}
              src={src}
              srcSet={srcSet}
              class={className}
              {...props}
            />
          ) : show ? (
            <BaseImg
              ref={ref}
              src={src}
              srcSet={srcSet}
              className={className}
              onLoad={onLoad}
              quality={quality}
              {...props}
            />
          ) : (
            <div ref={lazyRef} className={className} />
          )
        }
      </AmpContext.Consumer>
    );
  }
);

export default Img;

export const Placeholder = ({
  initial,
  width,
  ...props
}: {
  initial: string,
  width?: any
}) => (
  <Box
    position="relative"
    borderRadius="50%"
    bg={colors.platinum}
    color={colors.aluminum}
    width={width}
    {...props}
  >
    <Box pb="100%">
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
        position="absolute"
        top={0}
        left={0}
      >
        <Box
          fontSize={`${typeof width === 'number' ? (40 * width) / 64 : 64}px`}
          fontWeight={`${fontWeights.bold}`}
        >
          {initial[0].toUpperCase()}
        </Box>
      </Box>
    </Box>
  </Box>
);

const LogoBox = styled(Box)`
  img {
    max-width: 100%;
    max-height: 100%;
    font-size: 12px;
  }
`;

export const LogoImage = ({
  width = 64,
  height,
  fsWidth,
  fsHeight,
  handle,
  mimetype,
  src,
  initial,
  alt,
  round,
  fit,
  'data-qa-id': qaId,
  noPlaceholder,
  ...props
}: {
  width?: any,
  height?: any,
  fsWidth?: number,
  fsHeight?: number,
  handle?: string,
  mimetype?: string,
  src?: string,
  initial?: string,
  alt: string,
  round?: boolean,
  'data-qa-id'?: string,
  fit?: string,
  noPlaceholder?: boolean
}) => {
  const imageWidth = fsWidth || width;
  const imageHeight = fsHeight || height;
  return (
    <LogoBox
      height={height}
      width={width}
      display="flex"
      alignItems="center"
      justifyContent="center"
      {...props}
    >
      {handle ? (
        <Img
          data-qa-id={qaId}
          layout="responsive"
          src={getSrc({
            handle,
            width: imageWidth,
            height: imageHeight,
            round,
            fit
          })}
          srcSet={`${getSrc({
            handle,
            width: imageWidth,
            height: imageHeight,
            round,
            fit
          })},
                ${getSrc({
                  handle,
                  width: imageWidth ? imageWidth * 2 : undefined,
                  height: imageHeight ? imageHeight * 2 : undefined,
                  round,
                  fit,
                  format: mimetype === 'image/tiff' ? 'jpg' : undefined
                })} 2x,
                ${getSrc({
                  handle,
                  width: imageWidth ? imageWidth * 3 : undefined,
                  height: imageHeight ? imageHeight * 3 : undefined,
                  round,
                  fit,
                  format: mimetype === 'image/tiff' ? 'jpg' : undefined
                })} 3x`}
          alt={alt}
        />
      ) : src ? (
        <Img data-qa-id={qaId} src={src} alt={alt} />
      ) : (
        !noPlaceholder && (
          <Placeholder
            initial={initial || alt}
            width={
              typeof width === 'number' && typeof height === 'number'
                ? Math.min(width, height)
                : typeof fsWidth === 'number' && typeof fsHeight === 'number'
                ? Math.min(fsWidth, fsHeight)
                : width || height
            }
          />
        )
      )}
    </LogoBox>
  );
};

export const AvatarImage = ({
  alt,
  handle,
  initial,
  round = true,
  width = 64,
  ...props
}: {
  alt: string,
  handle?: string,
  initial?: string,
  round?: boolean,
  width?: number
}) => (
  <LogoImage
    width={width}
    height={width}
    handle={handle}
    initial={initial}
    alt={alt}
    fit="crop"
    round={round}
    {...props}
  />
);

const imageFadeIn = blurAmount => keyframes`
  0% {
    filter: blur(${blurAmount}px);
    transform: scale(1.05);
    opacity: 0;
  }

  33% {
    filter: blur(${blurAmount}px);
    transform: scale(1.05);
    opacity: 0.25;
  }

  100% {
    filter: blur(0);
    opacity: 1;
  }
`;

const LazyBox = styled(Box)`
  ${props =>
    !props.$preloaded &&
    props.$loaded &&
    css`
      img {
        animation: ${imageFadeIn(props.$blurAmount)} 1s forwards;
      }
    `}
  ${props =>
    !props.$preloaded &&
    !props.$loaded &&
    css`
      img {
        filter: blur(20px);
        transform: scale(1.05);
        opacity: 0;
      }
    `}
  img {
    display: block;
  }
`;

const animImagePath = amount => keyframes`
  0% {
    stroke-dashoffset: -${amount}px;
  }
  30%, 70% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: ${amount}px;
  }
`;

export const ImageSpinner = styled(props => (
  <Box {...props}>
    <Icon name="image-path" width={40} />
  </Box>
))`
  transition: all ${transition} 1s;
  svg {
    path,
    circle {
      stroke: ${colors.silver};
      fill: transparent;
    }
    path,
    polyline {
      stroke-dasharray: 167px;
      stroke-dashoffset: -167px;
      animation: ${animImagePath(167)} 2s infinite;
    }
    circle {
      stroke-dasharray: 30px;
      stroke-dashoffset: -30px;
      animation: ${animImagePath(30)} 2s infinite;
    }
  }
`;

const AmpImageWrap = styled.div`
  position: 'relative';
  width: 100%;
  ${({ ampHeight }) => `height: ${ampHeight}px;`}

  /* stylelint-disable */
  amp-img img {
    object-fit: contain;
    ${({ ampLeftPosition, ampTopPosition }) =>
      `
      object-position: ${ampLeftPosition} ${ampTopPosition};
    `}
  }
`;

export const FilestackImage = ({
  alt,
  data,
  height,
  width,
  round,
  fit,
  'data-qa-id': qaId,
  layout,
  format,
  quality,
  ampHeight,
  ampLeftPosition,
  ampTopPosition,
  ...props
}: {
  alt: string,
  data: ?ImageData,
  height?: number,
  width?: number,
  round?: boolean,
  fit?: string,
  layout?: string,
  format?: string,
  'data-qa-id'?: string,
  quality?: number,
  ampHeight?: number,
  ampLeftPosition?: string,
  ampTopPosition?: string
}) => {
  const handle = data ? data.handle : '';
  const mimetype = data ? data.mimetype : '';

  const src = getSrc({
    handle,
    width,
    height,
    round,
    fit,
    format: mimetype === 'image/tiff' ? 'jpg' : format,
    quality
  });
  const srcSet = `${getSrc({
    handle,
    width,
    height,
    round,
    fit,
    format: mimetype === 'image/tiff' ? 'jpg' : format,
    quality
  })},
    ${getSrc({
      handle,
      width: width ? width * 2 : undefined,
      height: height ? height * 2 : undefined,
      round,
      fit,
      format: mimetype === 'image/tiff' ? 'jpg' : format,
      quality
    })} 2x`;

  const [loaded, setLoaded] = useState(false);
  const [preloaded, setPreloaded] = useState(true);
  const [dimensions, setDimensions] = useState({
    width: 'auto',
    height: 'auto'
  });
  const imgEl = useRef(null);

  const onLoad = () => {
    setDimensions({
      width: imgEl.current ? imgEl.current.offsetWidth : 0,
      height: imgEl.current ? imgEl.current.offsetHeight : 0
    });
    setLoaded(true);
    setTimeout(() => {
      setDimensions({ width: 'auto', height: 'auto' });
    }, 3000);
  };

  useEffect(
    () => {
      if (imgEl && imgEl.current && imgEl.current.complete) {
        setPreloaded(true);
        setLoaded(true);
      } else {
        setPreloaded(false);
        setLoaded(false);
      }
    },
    [fit, handle, height, round, width]
  );

  if (!data) {
    return null;
  }

  return (
    <Box
      position="relative"
      overflow="hidden"
      borderRadius={round ? '50%' : undefined}
      {...props}
    >
      <NoAmp>
        <ImageSpinner
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          opacity={loaded ? 0 : 1}
        />
      </NoAmp>
      <LazyBox
        $loaded={loaded}
        $preloaded={preloaded}
        position="relative"
        width={dimensions.width}
        height={dimensions.height}
        $blurAmount={width ? width / 50 : height ? height / 50 : 20}
      >
        <Box position="relative">
          {round ? (
            <Img
              ref={imgEl}
              onLoad={onLoad}
              data-qa-id={qaId}
              height={height}
              width={width}
              src={src}
              srcSet={srcSet}
              alt={alt}
              layout={layout}
              quality={quality}
            />
          ) : (
            <>
              {!loaded && (
                <Box opacity={0}>
                  <Img
                    data-qa-id={qaId}
                    height={height}
                    width={width}
                    src={getSrc({
                      handle,
                      width: width ? Math.ceil(width / 30) : undefined,
                      height: height ? Math.ceil(height / 30) : undefined,
                      round,
                      fit
                    })}
                    alt={alt}
                    layout={layout}
                    alwaysShow
                  />
                </Box>
              )}
              {ampHeight ? (
                <AmpImageWrap
                  ampHeight={ampHeight}
                  ampLeftPosition={ampLeftPosition}
                  ampTopPosition={ampTopPosition}
                >
                  <Img
                    ref={imgEl}
                    onLoad={onLoad}
                    data-qa-id={qaId}
                    height={height}
                    width={width}
                    src={src}
                    srcSet={srcSet}
                    alt={alt}
                    layout={layout}
                    quality={quality}
                  />
                </AmpImageWrap>
              ) : (
                <Box
                  position={loaded ? undefined : 'absolute'}
                  width={loaded ? undefined : '100%'}
                  height={loaded ? undefined : '100%'}
                  top={0}
                  left={0}
                >
                  <Img
                    ref={imgEl}
                    onLoad={onLoad}
                    data-qa-id={qaId}
                    height={height}
                    width={width}
                    src={src}
                    srcSet={srcSet}
                    alt={alt}
                    layout={layout}
                    quality={quality}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </LazyBox>
    </Box>
  );
};
